import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Socket } from "socket.io-client";
import { getChangeLogImport } from "src/components/gametable/projectImports";
import { ProjectInfo, TableInfo } from "src/components/types";
import "./ProjectDetail.css";
import { Collapse } from "@mui/material";
import { ListAlt, TableRestaurantOutlined } from "@mui/icons-material";
import ProjectHeader from "src/components/ProjectHeader";
import TableCreator from "src/components/TableCreator";
import { canAdminProject, canSeeFeedback } from "src/components/utils";
import TableTile from "src/components/TableTile";
import TableList from "src/components/TableList";

function ProjectDetail(props: { socket: Socket; connected?: boolean; socketAmt: number }) {
  const [projectLoading, setProjectLoading] = useState(true);
  const [projectData, setProjectData] = useState<ProjectInfo>();
  const [changeLog, setChangeLog] = useState({});
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [myTables, setMyTables] = useState<TableInfo[]>([]);
  const [headerCollapsed, setHeaderCollapsed] = useState(false);
  const [createdTable, setCreatedTable] = useState<TableInfo>();
  const [visibleSection, setVisibleSection] = useState("none");

  const projectName = projectData?.project_name ?? "";

  let navigate = useNavigate();
  let params = useParams();
  let query = useSearchParams()[0];

  const fetchProjectDataAndTables = useCallback(
    async (status: string) => {
      return fetch(
        "/api/projects/get-project-detail/" +
          params.projectSlug +
          "?status=" +
          status +
          "&sessionId=" +
          sessionStorage.sessionId,
        {
          method: "GET",
        },
      )
        .then((response) => {
          if (response.ok) {
            return response.json();
          }
          return Promise.reject(response);
        })
        .catch((error) => {
          console.log(error);
        });
    },
    [params.projectSlug],
  );

  const fetchAndSetTables = useCallback(
    (status: string) => {
      fetch("/api/get-table-list/" + "?projectSlug=" + params.projectSlug + "&status=" + status, {
        method: "GET",
      })
        .then((response) => {
          console.log(response);
          if (response.ok) {
            return response.json();
          }
          return Promise.reject(response);
        })
        .then((data) => {
          setTables((tables) => [...tables, ...data]);
        });
    },
    [params.projectSlug],
  );

  const loadAndSetMoreTables = useCallback(
    (status: string, numberOfTables: number) => {
      fetch(
        "/api/get-table-list/" + "?projectSlug=" + params.projectSlug + "&status=" + status + "&skip=" + numberOfTables,
        {
          method: "GET",
        },
      )
        .then((response) => {
          if (response.ok) {
            return response.json();
          }
          return Promise.reject(response);
        })
        .then((data) => {
          if (status === "my") {
            setMyTables((tables) => [...tables, ...data]);
          } else {
            setTables((tables) => [...tables, ...data]);
          }
        });
    },
    [params.projectSlug],
  );

  const eraseCreatedTable = () => {
    if (!createdTable) {
      return;
    }
    fetch("/api/projects/" + params.projectSlug + "/erase-table/" + createdTable.tableId, {
      method: "GET",
    })
      .then((response) => {
        if (response.ok) {
          return response.text();
        }
        return Promise.reject(response);
      })
      .then((result) => {
        console.log(result);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  function addTable(tableData: TableInfo) {
    tableData.newCreated = true;
    setTables((tables) => [tableData].concat(tables));
    setCreatedTable({ ...tableData });
  }

  function loadMore(status: string, numberOfTables: number) {
    loadAndSetMoreTables(status, numberOfTables);
  }

  const tableCreated = useCallback(
    (tableDataSrc: string) => {
      let tableData: TableInfo = JSON.parse(tableDataSrc);
      if (tableData.projectInfo.slug === params.projectSlug) {
        addTable(tableData);
      }
    },
    [params.projectSlug],
  );

  useEffect(() => {
    setMyTables([]);
    setProjectData(undefined);
    setProjectLoading(true);
    fetchProjectDataAndTables("my")
      .then((res) => {
        if (res) {
          setMyTables(res?.tables);
          setProjectData(res);
          setProjectLoading(false);
        }
      })
      .catch((err) => {
        console.log(err);
        return;
      });

    fetchAndSetTables("open");
    fetchAndSetTables("midgame");
    fetchAndSetTables("ended");

    getChangeLogImport(params.projectSlug as string)
      .then((module) => {
        setChangeLog(module.default);
      })
      .catch((err) => {
        console.log(err);
        return;
      });
    return () => {
      const sessionId = sessionStorage.getItem("sessionId");
      fetch("/api/leave-lobby/" + params.projectSlug + (sessionId ? "?sessionId=" + sessionId : ""), {
        method: "GET",
      });
    };
  }, [fetchAndSetTables, fetchProjectDataAndTables, params.projectSlug]);

  useEffect(() => {
    function setupSocketListeners() {
      props.socket.on("table-create", tableCreated);
      props.socket.on("remove-table", tableRemoved);
      props.socket.on("table-started", tableStarted);
    }
    setupSocketListeners();
    return () => {
      props.socket.off("table-create", tableCreated);
      props.socket.off("remove-table", tableRemoved);
      props.socket.off("table-started", tableStarted);
      props.socket.off("reconnect");
    };
  }, [props.socket, tableCreated]);

  useEffect(() => {
    document.title = projectName + " - CGO";
  });

  useEffect(() => {
    if (query.get("openSection") === "join") {
      setHeaderCollapsed(true);
      setVisibleSection("join");
    }
  }, [query]);

  function tableRemoved(tableId: string) {
    for (let setter of [setTables, setMyTables]) {
      setter((tables) => {
        let newTables = [...tables];
        for (let t in newTables) {
          let table = newTables[t];
          if (table.tableId === tableId) {
            newTables.splice(+t, 1);
            break;
          }
        }
        return newTables;
      });
    }
    setCreatedTable((oldValue) => {
      if (oldValue?.tableId === tableId) {
        return undefined;
      }
      return oldValue;
    });
  }

  function tableStarted(tableId: string) {
    console.log("table starting", tableId);
    setTables((tables) => {
      let newTables = [...tables];
      for (let t in newTables) {
        let table = newTables[t];
        if (table.tableId === tableId) {
          table.status = "midgame";
        }
      }
      return newTables;
    });
    setCreatedTable((oldValue) => {
      if (oldValue?.tableId === tableId) {
        oldValue.status = "midgame";
        return { ...oldValue };
      }
    });
  }

  function createTableClickHandler() {
    setHeaderCollapsed(true);
    setVisibleSection("create");
  }

  function joinTableClickHandler() {
    setHeaderCollapsed(true);
    setVisibleSection("join");
  }

  if (projectLoading) {
    return <div className="project-main">Loading project...</div>;
  } else if (!projectData) {
    return <div className="project-main">Project data not found (not authorized?)</div>;
  } else {
    let bigButtonSx = {
      color: "white",
      fontSize: "32px",
      width: "50%",
      borderColor: "white",
      borderWidth: "2px",
      "&:hover": {
        color: "lightgray",
        borderColor: "lightgray",
        borderWidth: "2px",
      },
    };
    let iconSx = {
      width: "32px",
      height: "32px",
      marginRight: "1rem",
    };
    let openTableCount = tables.filter((table) => table.status === "open").length;
    return (
      <div className="project-detail-redesign" data-project={projectData.slug}>
        <img src={`../backgrounds/${projectData.slug}.webp`} className="project-detail-redesign-image-background" />
        <div className="project-detail-redesign-gradient-container" />
        <ProjectHeader
          projectData={projectData}
          changeLog={changeLog}
          collapsed={headerCollapsed}
          uncollapse={() => {
            setHeaderCollapsed(false);
          }}
        />
        <div className={"project-detail-redesign-buttons " + (headerCollapsed ? "collapsed" : "")}>
          <button className="project-detail-redesign-big-button" onClick={joinTableClickHandler}>
            <ListAlt sx={iconSx} />
            <span>Join a table{openTableCount > 0 ? ` (${openTableCount}\u00A0open)` : ""}</span>
          </button>
          <button className="project-detail-redesign-big-button" onClick={createTableClickHandler}>
            <TableRestaurantOutlined sx={iconSx} />
            Create new table
          </button>
        </div>
        <Collapse in={headerCollapsed && visibleSection === "create"}>
          <div className="project-detail-redesign-table-creator-container">
            <TableCreator
              roles={projectData.roles}
              projectSlug={params.projectSlug ?? ""}
              options={projectData.options}
              allAccess={canAdminProject(projectData) || canSeeFeedback(projectData)}
              tryErasePreviousTable={eraseCreatedTable}
            />
            {createdTable ? (
              <TableTile
                key={createdTable.tableId}
                projectName={projectName}
                dataSheet={projectData.dataSheet}
                tableInfo={createdTable}
                options={projectData.options}
                decks={projectData.decks}
                socket={props.socket}
                showAllOptions={canAdminProject(projectData) || canSeeFeedback(projectData)}
              />
            ) : (
              ""
            )}
          </div>
        </Collapse>
        <Collapse in={headerCollapsed && visibleSection === "join"}>
          <div className="project-detail-redesign-table-list-container">
            <TableList
              projectName={projectName}
              tables={tables}
              myTables={myTables}
              socket={props.socket}
              dataSheet={projectData.dataSheet}
              options={projectData.options}
              decks={projectData.decks}
              loadMore={loadMore}
              showAllOptions={canAdminProject(projectData) || canSeeFeedback(projectData)}
            />
          </div>
        </Collapse>
      </div>
    );
  }
}

export default ProjectDetail;
